// Explorer > one project: the conversation and everything around it.
//
// Two data sources, one set of markup (see lib/explorer-access.ts):
//
//   full    — GET /api/admin/projects/{id}. TALENT-BEARING: it carries
//             `sellerThreads`, the real de-anonymized concierge outreach. Gated
//             `talent_management` OR `explorer`, at `read`. One fetch
//             returns the whole record, so every tab is a lens on data already
//             in hand. VIEWER mode reads this same route and renders the same
//             tabs; it differs only in that it may not act (see below).
//   support — GET /api/admin/support/conversations/{id}. The narrow, talent-free
//             payload, assembled from an explicit server-side allowlist that
//             never touches the concierge tables.
//
// A support-mode viewer is not shown a trimmed version of the fat payload — they
// never fetch it, and it would 403 them if they tried. The missing tabs are
// missing because the data is genuinely absent, which is the boundary
// `web/routes/admin_support.py` exists to hold. Do NOT "simplify" this by having
// both modes read the fat route and hiding tabs in the browser.
//
// FILES are the one thing on this page that is NOT already in hand: the payload
// lists a file's name, size, and machine-extracted text, but bytes are not a JSON
// field. Each mode therefore hands the Files tab its OWN download fetcher, pointed
// at the route its viewer may actually call.
//
// The OPERATOR CONTROLS (Pause agent, Stop and notify client — the manual
// big-fish takeover that hands CS the BigQuery handoff row — and Approve brief,
// which opens the search gate and starts the match for the client) render in
// FULL mode only, in the header, and the Conversation tab then carries the
// reply-as-Mira composer. Neither support nor VIEWER mode gets them, for the
// same reason from
// two directions: `web/routes/admin_support.py` is read-only BY DESIGN, and the
// five operator writes are gated `explorer OR talent_management` at FULL access
// (migration 0119, levelled by 0120), so a read-only holder is refused by the
// server as well as by this page. Speaking to a client as Mira is a policy
// decision about who may be the product's voice, not a UI gap. So the mode does
// not merely pick a payload
// here, it picks whether this page can act at all.
//
// ONE signal carries that: a null `onChanged`. There is no re-read to schedule
// when nothing can change, so every control on the page hangs off it rather than
// off a mode comparison of its own — add a write here and it inherits the rule.
import {
  Badge,
  Button,
  Card,
  TableExport,
  usePusherChannel,
  useRestoredState,
} from "@recruiter/shared";
// The thread renderer takes the WIRE message shape (nullable text), which both
// payloads satisfy: the fat route's domain ChatMessage narrows into it.
import type {
  AdminProjectArm,
  AdminExportReady,
  AdminExportResponse,
  SupportChatMessage,
} from "@recruiter/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApproveBriefButton } from "../components/approve-brief-button";
import { AreaBoundary } from "../components/area-gate";
import { BackLink } from "../components/back-link";
import { ConversationThread } from "../components/conversation-thread";
import { TranslatableConversation } from "../components/translatable-conversation";
import { OperatorControls } from "../components/operator-controls";
import { OperatorConversation } from "../components/operator-conversation";
import { ProjectSearchPicks } from "../components/project-search-picks";
import { ProjectSellers } from "../components/project-sellers";
import { ShortlistManager } from "../components/shortlist-manager";
import { ProjectStageBadge } from "../components/project-stage-badge";
import { SellerIntentTag } from "../components/seller-intent-tag";
import { WorkShapeTag } from "../components/work-shape-tag";
import { ModelTierTag } from "../components/model-tier-tag";
import { EmptyState, LoadingState } from "../components/states";
import {
  ApiError,
  type BriefChecklistView,
  type ExplorerProjectDetail,
  type ExplorerProjectMeeting,
  fetchAdminExport,
  fetchExplorerProjectFile,
  fetchProjectBriefHtml,
  fetchSupportProjectFile,
  requestProjectBlueprintExport,
  requestProjectBriefExport,
} from "../lib/api";
import { canAct, useExplorerMode } from "../lib/explorer-access";
import { formatDateTime } from "../lib/format";
import { MEETING_VERIFICATION_LABEL, MEETING_VERIFICATION_TONE } from "../lib/meeting-verification";
import { debugChannelFor, employeePusherConfig } from "../lib/pusher";
import {
  adminKeys,
  conversationQuery,
  explorerProjectQuery,
  meQuery,
  projectArmsQuery,
  projectExperimentsQuery,
} from "../lib/queries";
import { useHoldsAnyArea } from "../lib/use-access";
import "./explorer.css";

const TABS = [
  "Conversation",
  "Brief",
  "Files",
  "Identity",
  "Search prefs",
  "Sellers",
  "Meetings",
] as const;
type Tab = (typeof TABS)[number];

/** The tabs a SUPPORT-mode viewer gets — see the module header for why the rest
 *  are absent rather than hidden. */
const SUPPORT_TABS: readonly Tab[] = ["Conversation", "Brief", "Files"];

/** Why a file has no Download button. Bytes are only servable once the antivirus
 *  gate clears them, so the row names the state it is stuck in rather than
 *  offering a button the route would answer with 423. */
const SCAN_LABELS: Record<string, string> = {
  pending: "Scanning",
  infected: "Blocked",
  error: "Scan failed",
};

/** Narrow one key of a `to_wire()` dict to an array without trusting it. The
 *  legacy routes are hand-mirrored (see lib/api.ts), so a shape change lands
 *  here as a missing key rather than a type error — degrade to empty, never
 *  throw mid-render. */
function asArray(source: Record<string, unknown> | null, key: string): Record<string, unknown>[] {
  const value = source?.[key];
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

function text(row: Record<string, unknown> | null, ...keys: string[]): string | null {
  if (!row) return null;
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

interface DetailFlag {
  id: string;
  reason: string;
  detail: string | null;
}
interface DetailSection {
  id: string;
  title: string;
  content: string;
}

/** How complete the brief is, done/not-done per row. Computed server-side by the
 *  same function the client's own right rail and the search gate read, so this
 *  panel reports the client's checklist rather than a second opinion about it.
 *  Null in support mode, whose narrower payload does not carry it. */
type Checklist = BriefChecklistView | null;
interface DetailFile {
  id: string;
  name: string;
  size: string;
  uploadedAt: string | null;
  /** The antivirus gate (`pending` | `clean` | `infected` | `error`). Only a
   *  `clean` file has servable bytes — see `SCAN_LABELS`. */
  scanStatus: string;
}

/** Labelled key/value rows for a record whose exact fields we deliberately do
 *  not type — an audit view should surface a field the backend adds tomorrow. */
function FactList({ record }: { record: Record<string, unknown> | null }) {
  const entries = Object.entries(record ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (entries.length === 0) return <p className="ex-muted">Nothing captured yet.</p>;
  return (
    <dl className="ex-facts">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key.replaceAll("_", " ")}</dt>
          <dd>
            {typeof value === "object" ? (
              <pre className="ex-json">{JSON.stringify(value, null, 2)}</pre>
            ) : (
              String(value)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The client's brief checklist: which rows Mira has captured and which she is
 *  still chasing.
 *
 *  This is the answer to "why has this client not been offered the search yet?",
 *  which is otherwise only visible by reading the whole conversation. REQUIRED
 *  rows come first because they are the ones that gate: the client's approve
 *  control unlocks the moment every one of them is done.
 *
 *  `searchEnabled` is shown only when it DISAGREES with the live count. It is a
 *  latch (set once every required row was done, never retracted), so the two can
 *  legitimately differ after a later verdict regression, and that gap is exactly
 *  what an operator looking at a stuck client needs to see.
 *
 *  The note this panel used to print on that gap said the disagreement was
 *  harmless because the unlock could never close again. That was false: the
 *  client's control was ALSO gated on the live count, so the gap WAS the
 *  outage, and operators reading this panel were told to look past the one
 *  thing they should have been looking at. The gate now matches what the note
 *  claimed, and the note says what the gap actually costs. */
function ChecklistPanel({ checklist }: { checklist: BriefChecklistView }) {
  const { requiredDone, requiredTotal, optionalDone, optionalTotal } = checklist;
  const latchDisagrees = checklist.searchEnabled && !checklist.allRequiredDone;

  return (
    <Card padding="lg" className="ex-checklist">
      <div className="ex-checklist__head">
        <h3 className="ad-card__title">Brief checklist</h3>
        <Badge tone={checklist.allRequiredDone ? "success" : "warning"} size="sm">
          {requiredDone}/{requiredTotal} required
        </Badge>
        {optionalTotal > 0 ? (
          <Badge tone="neutral" size="sm">
            {optionalDone}/{optionalTotal} optional
          </Badge>
        ) : null}
        {checklist.templateAuthored ? null : (
          <span
            className="rc-cell-sub"
            title="Atlas has not tailored the rows to this client's domain yet, so these are the generic ones."
          >
            default rows
          </span>
        )}
      </div>

      {latchDisagrees ? (
        <p className="ex-checklist__latch">
          Search is unlocked for this client even though a required row is now open. Their approve
          control is still on screen: it follows the unlock, which latches on and never retracts.
          The open row is Mira's next question, not a block. If the row has never been asked (no
          verdict yet, such as a Location row added after the brief was finished), Mira asks it out
          loud and Atlas holds back the approve popup until she has. Nothing here stops the client
          continuing.
        </p>
      ) : null}

      <ul className="ex-checklist__rows">
        {/* Required rows first: they are the ones that gate. A stable sort keeps
            the template's own order within each group, which is the order Mira
            asks in. */}
        {[...checklist.items]
          .sort((a, b) => Number(b.required) - Number(a.required))
          .map((item) => (
            <li
              key={item.key}
              className={`ex-checklist__row${item.done ? " ex-checklist__row--done" : ""}`}
            >
              {/* The tick is decoration; the state is read out inside the label,
                  so a screen reader gets it as part of the row rather than as a
                  loose word. Exactly three grid children, always. */}
              <span className="ex-checklist__mark" aria-hidden="true">
                {item.done ? "✓" : "·"}
              </span>
              <span className="ex-checklist__label">
                {item.label}
                {/* NOT `rc-cell-sub`: that class is display:block and drops the
                    tag onto its own line under the label. */}
                {item.required ? null : <span className="ex-checklist__optional">optional</span>}
                <span className="ad-visually-hidden">{item.done ? " done" : " not done"}</span>
              </span>
              {/* MASON's one-line why. The verdict alone says a row is open;
                  the reason says what it is waiting for. */}
              {item.reason ? <span className="ex-checklist__reason">{item.reason}</span> : null}
            </li>
          ))}
      </ul>

      {checklist.note ? (
        <p className="ex-checklist__note">
          <strong>Note for Mira:</strong> {checklist.note}
        </p>
      ) : null}
    </Card>
  );
}

function BriefTab({ sections, checklist }: { sections: DetailSection[]; checklist: Checklist }) {
  if (sections.length === 0 && !checklist) {
    return (
      <EmptyState title="No brief yet" message="Nothing has been captured for this project." />
    );
  }
  return (
    <div className="ex-stack">
      {/* Above the sections on purpose: what is still MISSING is the operator's
          first question, and the sections below are what has landed. */}
      {checklist ? <ChecklistPanel checklist={checklist} /> : null}
      {sections.length === 0 ? (
        <Card padding="lg">
          <p className="ex-muted">No brief sections have been written yet.</p>
        </Card>
      ) : null}
      {sections.map((section) => (
        <Card padding="lg" key={section.id}>
          <h3 className="ad-card__title">{section.title}</h3>
          <p className="ex-prose">{section.content || "Nothing captured yet."}</p>
        </Card>
      ))}
    </div>
  );
}

/** Save fetched bytes to disk. The download is an authed FETCH, not a navigation
 *  (lib/api.ts: the admin identity rides a header plus a cross-origin cookie, which
 *  a plain link would not carry), so by the time we get here the file is in memory
 *  and this synthetic click is the browser's only "save it" primitive. */
function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next task, not inline: a synchronous revoke can race the
  // browser's own read of the object URL and cancel the save.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function FilesTab({
  files,
  download,
}: {
  files: DetailFile[];
  /** Mode-specific: each view hands in the fetcher for the route that will
   *  actually succeed for its viewer (lib/explorer-access.ts). */
  download: (fileId: string) => Promise<Blob>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ id: string; detail: string } | null>(null);

  async function onDownload(file: DetailFile) {
    setBusyId(file.id);
    setFailure(null);
    try {
      saveBlob(await download(file.id), file.name);
    } catch (error) {
      // Surfaced, never swallowed (doctrine 1.5). The detail is the backend's:
      // 423 says the antivirus scan has not cleared this file, 403 that this role
      // cannot read it, 404 that the bytes are gone.
      setFailure({
        id: file.id,
        detail: error instanceof ApiError ? error.detail : "Download failed.",
      });
    } finally {
      setBusyId(null);
    }
  }

  if (files.length === 0) return <EmptyState title="No files" />;
  return (
    <div className="rc-table-wrap">
      <table className="rc-table">
        <TableExport name="project-files" label="project files" />
        <thead>
          <tr>
            <th>File</th>
            <th>Size</th>
            <th>Uploaded</th>
            <th>Download</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <tr key={file.id}>
              <td>{file.name}</td>
              <td>{file.size}</td>
              <td>{formatDateTime(file.uploadedAt)}</td>
              <td>
                {file.scanStatus === "clean" ? (
                  <Button
                    variant="outline"
                    tone="neutral"
                    size="sm"
                    disabled={busyId === file.id}
                    onClick={() => void onDownload(file)}
                  >
                    {busyId === file.id ? "Fetching…" : "Download"}
                  </Button>
                ) : (
                  <span className="ex-muted">
                    {SCAN_LABELS[file.scanStatus] ?? file.scanStatus}
                  </span>
                )}
                {failure?.id === file.id ? <p className="ex-file-error">{failure.detail}</p> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CLAIM_SOURCE_LABEL: Record<string, string> = {
  slot_pick: "Picked a slot",
  already_booked_claim: "Said they already booked",
};

/** Every specialist-call booking claim on this project (`meetings` table,
 *  migration 0182) — the full history behind the Big-clients lane's single
 *  derived "booked / not booked" bit. A claim is not a confirmation: the
 *  verification badge says which tier it is in, and only a vendor read (never
 *  a client's own click) can ever move a row to Confirmed or Canceled. */
function MeetingsTab({ meetings }: { meetings: ExplorerProjectMeeting[] }) {
  if (meetings.length === 0) {
    return (
      <EmptyState
        title="No meetings"
        message="This project was never routed to a desk, or no claim has been made yet."
      />
    );
  }
  return (
    <div className="rc-table-wrap">
      <table className="rc-table">
        <TableExport name="project-handoffs" label="handoffs" />
        <thead>
          <tr>
            <th>Desk</th>
            <th>Vendor</th>
            <th>Verification</th>
            <th>Slot</th>
            <th>Duration</th>
            <th>Claim source</th>
            <th>Claimed</th>
            <th>Confirmed</th>
            <th>Canceled</th>
            <th>Booking link</th>
          </tr>
        </thead>
        <tbody>
          {meetings.map((meeting) => (
            <tr key={meeting.id}>
              <td>{meeting.track}</td>
              <td>{meeting.vendor}</td>
              <td>
                <Badge
                  tone={MEETING_VERIFICATION_TONE[meeting.verification] ?? "neutral"}
                  size="sm"
                >
                  {MEETING_VERIFICATION_LABEL[meeting.verification] ?? meeting.verification}
                </Badge>
              </td>
              <td>{meeting.slotStartUtc ? formatDateTime(meeting.slotStartUtc) : "-"}</td>
              <td>{meeting.durationMinutes ? `${meeting.durationMinutes} min` : "-"}</td>
              <td>{CLAIM_SOURCE_LABEL[meeting.claimSource] ?? meeting.claimSource}</td>
              <td>{formatDateTime(meeting.claimedAt)}</td>
              <td>{meeting.confirmedAt ? formatDateTime(meeting.confirmedAt) : "-"}</td>
              <td>{meeting.canceledAt ? formatDateTime(meeting.canceledAt) : "-"}</td>
              <td>
                {meeting.bookingUrl ? (
                  <a href={meeting.bookingUrl} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  "-"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface DetailViewProps {
  projectId: string;
  projectName: string;
  owner: { id: string; label: string; email: string | null };
  agentPaused: boolean;
  humanTakeover: boolean;
  /** How far the project got, off the same ladder the Explorer's tables badge —
   *  so the pill here and the pill on its row can never disagree. Null in
   *  support mode, whose payload does not carry it. */
  stage: string | null;
  /** The engagement shape the agents latched (project / ongoing / hybrid) —
   *  the same tag the Explorer tables show. Null = not classified, or a
   *  payload that does not carry it. */
  workShape: string | null;
  /** The regional model tier the project was created on (luna / terra, migration
   *  0195) and the browser-detected country it was decided from — the same tag
   *  the Explorer tables show. Null = an older row, or a payload without it. */
  modelTier: string | null;
  originCountry: string | null;
  /** The client clicked a "search Fiverr yourself" link at least once. A header
   *  badge: "we showed a shortlist and they left to self-search" is a project
   *  fact an operator reads at a glance, not a metric. False in support mode,
   *  whose payload does not carry it. */
  selfSearchClicked: boolean;
  openFlags: DetailFlag[];
  /** Talent pitching their services, not a client buying them. Read from
   *  the whole flag history, so it outlives the alert it was raised with. */
  sellerIntent: boolean;
  messages: SupportChatMessage[];
  briefSections: DetailSection[];
  briefChecklist: Checklist;
  files: DetailFile[];
  /** The owner's Pusher channel, for live spectate. Null in support mode (which
   *  gets the read-only thread) or when the project has no derivable owner. */
  channel: string | null;
  /** Re-read the project after a write. Null in support and viewer modes, which
   *  is the signal that this view is READ-ONLY: no controls, no composer. See the
   *  module header for why that is a policy boundary and not a missing feature. */
  onChanged: (() => void) | null;
  /** Say so, when the reason this page has no controls is the VIEWER's grant
   *  rather than the surface. A support rep works a read-only console throughout
   *  and needs no label; someone in viewer mode is looking at the page an
   *  operator acts on, and an unexplained absence reads as a bug or an outage.
   *  Null everywhere else. */
  readOnlyLabel: string | null;
  /** Fetch one of this project's files as bytes, over the route THIS viewer may
   *  call. One of the two per-mode behaviours on the page; everything else is a
   *  lens on a payload already fetched. */
  downloadFile: (fileId: string) => Promise<Blob>;
  /** Whether this viewer may ask for an English reading of the conversation.
   *
   *  The other per-mode behaviour, and per-mode for the same reason: translating
   *  is gated on the route that serves the FULL project payload, and CS reads a
   *  different, narrower one (`web/routes/admin_support.py`, read-only by design
   *  — queuing a model call there is a spend decision nobody has taken). Support
   *  mode would otherwise render a button that 403s on every press, which is
   *  worse than not offering it. */
  canTranslate: boolean;
  tabs: readonly Tab[];
  /** The fat payload, or null in support mode. The talent-bearing tabs read from
   *  it and are simply not offered when it is absent. */
  full: ExplorerProjectDetail | null;
}

function TabPanel({ tab, props }: { tab: Tab; props: DetailViewProps }) {
  const { full } = props;
  switch (tab) {
    case "Conversation":
      // `onChanged` present = a viewer who may write, so the thread comes with the
      // reply box and live spectate. Absent = support mode, read-only by policy.
      return props.onChanged ? (
        <OperatorConversation
          projectId={props.projectId}
          channel={props.channel}
          agentPaused={props.agentPaused}
          messages={props.messages}
          clientName={props.owner.label}
          onChanged={props.onChanged}
        />
      ) : props.canTranslate ? (
        <TranslatableConversation
          projectId={props.projectId}
          messages={props.messages}
          clientName={props.owner.label}
        />
      ) : (
        <ConversationThread messages={props.messages} clientName={props.owner.label} />
      );
    case "Brief":
      return <BriefTab sections={props.briefSections} checklist={props.briefChecklist} />;
    case "Files":
      return <FilesTab files={props.files} download={props.downloadFile} />;
    case "Identity":
      // The record below is USER-scoped (one identity per account, reused across
      // every project that user owns - buyer_identity.py's contract), so the same
      // facts render on all of a user's projects, and a fact captured on an
      // earlier project (company, industry, site) legitimately appears here on a
      // later, unrelated one. Without the caption, that reads as "the page kept
      // the first project's details" (Monday 3187516446). Whether the identity
      // SHOULD accrete across projects is an open product question (Monday
      // 3180263711); this tab's job is only to stop presenting account data as
      // project data.
      return (
        <div className="ex-stack">
          <p className="ex-muted">
            Account-level identity: one record per user, shared by every project they own. Facts
            here can come from earlier projects. For what THIS project captured, read the Brief tab.
          </p>
          <FactList record={full?.buyerIdentity ?? null} />
        </div>
      );
    case "Search prefs":
      return <FactList record={full?.searchPreferences ?? null} />;
    case "Sellers":
      // TWO things, because "who did we find for this project" has two answers
      // and only one of them used to be here. The threads are the concierge
      // OUTREACH; the picks are SCOUT's shortlist, which is all there is for a
      // client who ran the self-serve search (no concierge run, so no threads,
      // so this tab was empty for them). Each renders nothing when it has
      // nothing, and the picks block owns the empty state for the case where
      // genuinely no search has run.
      return (
        <div className="ex-stack">
          {/* A write (it contacts a talent and pins their offer), so it follows
              the page's read-only signal. A viewer reads the threads and the
              picks below; the add/remove form is not theirs. */}
          {props.onChanged ? <ShortlistManager projectId={props.projectId} /> : null}
          <ProjectSellers runs={full?.sellerThreads ?? []} />
          <ProjectSearchPicks
            searches={full?.scoutSearches ?? []}
            total={full?.searchCount ?? 0}
            hasThreads={(full?.sellerThreads?.length ?? 0) > 0}
          />
        </div>
      );
    case "Meetings":
      return <MeetingsTab meetings={full?.meetings ?? []} />;
  }
}

/** A project name reduced to something safe to hand a filesystem. Never empty: a
 *  project is created before Mira has anything to name it after, so a large share of
 *  real rows carry a blank or punctuation-only name (lib/project-title.ts). */
function fileSlug(projectName: string): string {
  return (
    projectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "project"
  );
}

// One bounded wait, not a poll: Pusher never replays a one-shot event missed
// during a disconnect, so if nothing arrives the button must still leave its
// spinner. 90s is well past a healthy render (~8s) plus a cold browser lease.
const ADMIN_EXPORT_WATCHDOG_MS = 90_000;

// How many watchdog windows one click may wait through. Each expiry re-POSTs, and a
// route that answers without a download URL is saying the job is still alive — so the
// wait extends rather than failing over work in progress. Two windows (three minutes)
// covers a queue backlog plus the blueprint's thousand payload reads; past that the
// operator gets an answer instead of a spinner, because an unbounded re-arm is not a
// watchdog, it is the poll doctrine 1.2 forbids.
const ADMIN_EXPORT_MAX_WAITS = 2;

/** The operator-export handshake, shared by both export buttons on this page.
 *
 *  Both ask for something the server cannot produce on a request cycle (doctrine 1.1):
 *  the brief PDF drives a headless browser, and the debug blueprint rehydrates one
 *  storage object per captured agent step. Both therefore POST, get a 202 and a turn
 *  id, and wait for `admin.export.ready` on the operator's own Pusher channel.
 *  Everything about that wait — the drain, the watchdog, the reconcile — is identical
 *  between them and subtle enough (see below) that two copies would be two chances to
 *  get one of them wrong. The callers supply only what genuinely differs: how to ask,
 *  what to do with the bytes, and what to do when there are none.
 *
 *  Failure state stays with the CALLER rather than here, because the two buttons answer
 *  a missing artifact differently: the PDF has a real degraded form (the same brief as
 *  HTML) and the blueprint has none. A hook that owned the message would have to be
 *  told which, which is the same branch in a worse place.
 *
 *  No `usePusherHold`, unlike the talent's twin: `employeePusherConfig` sets
 *  `idleDisconnectMs: 0`, so this socket already survives a backgrounded tab. */
function useAdminExport({
  request,
  onReady,
  onUnavailable,
  onError,
}: {
  /** POST the export. Resolves with a turn to await, or with `url` already set when
   *  the bytes exist and this click collapsed onto a finished run. */
  request: () => Promise<AdminExportResponse>;
  /** The artifact exists: fetch it from `url` and hand it over. Owns its own failure —
   *  one caller is a Pusher handler with nobody waiting on its promise, so a rejection
   *  there would leave the button idle with neither a file nor a reason. */
  onReady: (url: string) => Promise<void>;
  /** The run finished (or timed out) with no artifact, and none is coming. */
  onUnavailable: (reason: string) => Promise<void>;
  /** The POST itself failed, so nothing was ever started. */
  onError: (reason: string) => void;
}) {
  const me = useQuery(meQuery);
  const email = me.data?.email ?? null;
  const [busy, setBusy] = useState(false);
  // What this button is waiting on. STATE, not a ref, and that is load-bearing: the
  // ready event can arrive BEFORE the POST that names its turn resolves, so learning
  // the turn id has to re-run the drain below over events already in hand.
  //
  // It carries an `attempt` beside the turn because the watchdog can decide to keep
  // waiting on the SAME turn, and two `setState` calls back to the same string are no
  // state change at all — React would re-render nothing and the timer below would
  // never re-arm. A fresh object always re-runs the effect.
  const [wait, setWait] = useState<{ turn: string; attempt: number } | null>(null);
  const awaitingTurn = wait?.turn ?? null;
  // Turns already resolved, so a re-scan of the buffer cannot download twice.
  const handledRef = useRef<Set<string>>(new Set());

  const events = usePusherChannel<AdminExportReady>(
    email ? debugChannelFor(email) : null,
    ["admin.export.ready"],
    employeePusherConfig,
  );

  // Find the event for the turn WE are waiting on. The channel is the EMPLOYEE's, not
  // this button's: another project's export — or the other button's, or a second
  // tab's — publishes here too, and resolving on one of those would save the wrong
  // file. Addressing by turn makes that structural rather than a guard.
  //
  // It SEARCHES the whole accumulated buffer rather than draining new events past a
  // cursor, because the two orderings are both real. A click that collapses onto a
  // nearly-finished run can see `admin.export.ready` land in the gap between the
  // worker publishing and the 202 arriving — and the worker's publish is
  // seq=1-guarded, so an event dropped in that gap never comes again and the button
  // spins to the watchdog. A cursor marked exactly those events consumed. Re-running
  // on `awaitingTurn` is what lets a late-bound turn collect an early event; the
  // hook's own array is the buffer, so there is none to keep here.
  useEffect(() => {
    if (!awaitingTurn || handledRef.current.has(awaitingTurn)) return;
    const ev = events.find((e) => e?.turn_id === awaitingTurn);
    if (!ev) return;
    handledRef.current.add(awaitingTurn);
    setWait(null);
    setBusy(false);
    if (ev.ok && ev.url) void onReady(ev.url);
    else void onUnavailable(ev.error ?? "The export failed.");
  }, [awaitingTurn, events, onReady, onUnavailable]);

  /** The watchdog fired: no event arrived in time. ASK before giving up.
   *
   *  "No event" does not mean "no artifact". The event is one-shot and seq=1-guarded,
   *  so anything that kept us from hearing it — a subscription not yet established, a
   *  socket blip, a drop while the auth POST was in flight — loses it forever while a
   *  perfectly good artifact sits in the bucket. Re-POSTing is the REST reconcile
   *  behind that: the route collapses onto the same turn and hands back the URL when
   *  the bytes exist.
   *
   *  Three outcomes, and the middle one is the reason this returns a verdict instead
   *  of just giving up:
   *  - **the bytes exist** — take them, done;
   *  - **no url came back** — the run is STILL GOING (the route collapsed onto it) or a
   *    dead-end turn was just replaced with a fresh one that is now running. Either way
   *    something is on its way, so keep waiting on the turn the route named rather than
   *    reporting failure over a live job. Abandoning here was survivable while the only
   *    caller degraded to an HTML brief; the blueprint has no fallback by design, and it
   *    is the likelier of the two to outrun 90s (queue wait, then up to a thousand
   *    payload objects), so it would have been the one left with nothing;
   *  - **the POST itself failed** — nothing to wait for. */
  const reconcile = useCallback(async (): Promise<
    { kind: "done" } | { kind: "waiting"; turn: string } | { kind: "unavailable"; reason: string }
  > => {
    try {
      const ack = await request();
      if (ack.url) {
        await onReady(ack.url);
        return { kind: "done" };
      }
      return { kind: "waiting", turn: ack.turn_id };
    } catch (error) {
      // Not swallowed: it becomes what the unavailable path reports.
      return {
        kind: "unavailable",
        reason: error instanceof ApiError ? error.detail : "The export took too long.",
      };
    }
  }, [onReady, request]);

  // Release the spinner if no event ever lands, and still give them an answer. Armed
  // per (turn, attempt), so resolving one disarms its timer and a re-wait arms a new
  // one. Bounded by `ADMIN_EXPORT_MAX_WAITS` — a reconcile that keeps finding the job
  // alive may extend the wait, never renew it forever.
  useEffect(() => {
    if (!wait) return;
    const id = window.setTimeout(() => {
      void (async () => {
        // The drain may have resolved this turn between the timer firing and this
        // body running; it already reported, so there is nothing left to do.
        if (handledRef.current.has(wait.turn)) return;
        // EVERY window reconciles, including the last one. The bound is on how many
        // times we may go back to WAITING, not on how many times we may ask — asking
        // is the cheap REST check that turns "no event" back into the artifact.
        const verdict = await reconcile();
        if (
          verdict.kind === "waiting" &&
          wait.attempt + 1 < ADMIN_EXPORT_MAX_WAITS &&
          !handledRef.current.has(verdict.turn)
        ) {
          setWait({ turn: verdict.turn, attempt: wait.attempt + 1 });
          return;
        }
        setWait(null);
        setBusy(false);
        if (verdict.kind === "waiting") await onUnavailable("The export took too long.");
        else if (verdict.kind === "unavailable") await onUnavailable(verdict.reason);
      })();
    }, ADMIN_EXPORT_WATCHDOG_MS);
    return () => window.clearTimeout(id);
  }, [wait, onUnavailable, reconcile]);

  const start = useCallback(async () => {
    setBusy(true);
    try {
      const ack = await request();
      if (ack.url) {
        // The bytes already existed and the response carried them, so this click
        // collapsed onto a finished run and no event is coming — the worker's
        // publish is seq=1-guarded and already fired for this turn.
        setWait(null);
        setBusy(false);
        await onReady(ack.url);
        return;
      }
      // Binding the turn HERE, after the round trip, is exactly why the drain
      // searches rather than drains: its event may already be sitting in the buffer.
      setWait({ turn: ack.turn_id, attempt: 0 });
    } catch (error) {
      // Surfaced, never swallowed (doctrine 1.5).
      setWait(null);
      setBusy(false);
      onError(error instanceof ApiError ? error.detail : "Export failed.");
    }
  }, [onError, onReady, request]);

  // `email` rides back because every caller must refuse to start before the identity
  // that names our channel is known: the subscription is skipped while it is null, so
  // a click in that window publishes its one-shot ready event to nobody. The watchdog
  // would recover it 90s later; not starting is better than recovering.
  return { busy, email, start };
}

/** Download the brief as the DOCUMENT the client was handed, not as data.
 *
 *  The counterpart to "Export debug" beside it, and deliberately a separate control
 *  rather than a format toggle on it: that one is the machine-readable blueprint an
 *  engineer re-imports, this is the branded page a client or talent actually reads,
 *  which is what an operator on a support call needs to be looking at.
 *
 *  Rendered OFF the request cycle (doctrine 1.1); `useAdminExport` owns the handshake.
 *  The synchronous route this replaced drove headless Chrome inside the request and
 *  lost to the 10s server timeout about half the time.
 *
 *  When the render cannot happen at all (no renderer on a local stack, a soft-failed
 *  render) it degrades to the same document as HTML, so an operator still walks away
 *  with the brief rather than a dead button. */
function BriefDocumentButton({
  projectId,
  projectName,
}: {
  projectId: string;
  projectName: string;
}) {
  const [failure, setFailure] = useState<string | null>(null);
  const request = useCallback(() => requestProjectBriefExport(projectId), [projectId]);

  /** No PDF to hand over. Save the HTML document instead so the operator is not
   *  left empty-handed (doctrine 1.5 — degrade, don't dead-end). The last resort:
   *  it swallows nothing, it just has nowhere further to fall. */
  const fallBackToHtml = useCallback(
    async (reason: string) => {
      try {
        saveBlob(await fetchProjectBriefHtml(projectId), `brief-${fileSlug(projectName)}.html`);
      } catch (error) {
        setFailure(error instanceof ApiError ? error.detail : reason);
      }
    },
    [projectId, projectName],
  );

  /** Fetch the rendered bytes and save them. Handles its OWN failure because one
   *  caller is a Pusher event handler with no try/catch around it (and no user
   *  waiting on a promise): a rejected fetch there would leave the button idle
   *  with neither a file nor a reason, and take an unhandled rejection with it. */
  const saveDocument = useCallback(
    async (url: string) => {
      try {
        saveBlob(await fetchAdminExport(url), `brief-${fileSlug(projectName)}.pdf`);
      } catch (error) {
        // The PDF rendered but its bytes would not come back (an expired session,
        // a wiped artifact). The HTML document is the same brief, so take the
        // same degrade the render failure takes rather than dead-ending here.
        await fallBackToHtml(
          error instanceof ApiError ? error.detail : "The PDF could not be downloaded.",
        );
      }
    },
    [fallBackToHtml, projectName],
  );

  const onError = useCallback((reason: string) => setFailure(reason), []);
  const { busy, email, start } = useAdminExport({
    request,
    onReady: saveDocument,
    onUnavailable: fallBackToHtml,
    onError,
  });

  return (
    <Button
      variant="outline"
      tone="neutral"
      size="sm"
      // Not clickable before the identity that names our channel is known — see
      // `useAdminExport`'s closing note.
      disabled={busy || !email}
      title={
        failure ??
        (email
          ? "Download the brief as the document the client receives"
          : "Loading your employee identity…")
      }
      onClick={() => {
        setFailure(null);
        void start();
      }}
    >
      {busy ? "Preparing…" : "Download brief PDF"}
    </Button>
  );
}

/** Download this project's full agent-debug bundle as JSON — brief (sections/blocks) PLUS
 *  the complete agent_trace (every LLM step: prompts, tool calls, tokens, cost). Read-only,
 *  every env; the counterpart Import (dev only) lives on the Explorer list page.
 *
 *  Off the request cycle for the same doctrine-1.1 reason as the PDF beside it, and with
 *  a different slow dependency: since the trace-offload migration each captured step's
 *  payload is its own storage object, so a bundle is up to a thousand object reads. The
 *  synchronous GET this replaced averaged 8.9s against a 10s server timeout in prod and
 *  returned 504 in bursts.
 *
 *  Unlike the PDF it has NO degraded form to fall back to. Half a bundle is not a smaller
 *  bundle, it is a bundle that re-imports as a different project, so a failure is
 *  reported rather than papered over. */
function ExportProjectButton({
  projectId,
  projectName,
}: {
  projectId: string;
  projectName: string;
}) {
  const [failure, setFailure] = useState<string | null>(null);
  const request = useCallback(() => requestProjectBlueprintExport(projectId), [projectId]);

  const saveBundle = useCallback(
    async (url: string) => {
      try {
        saveBlob(await fetchAdminExport(url), `debug-${fileSlug(projectName)}.json`);
      } catch (error) {
        setFailure(
          error instanceof ApiError ? error.detail : "The bundle could not be downloaded.",
        );
      }
    },
    [projectName],
  );

  // One sink, taken twice: with no degraded form to fall back to, "the run produced
  // nothing" and "the request never started" are the same thing to this button — a
  // sentence on it. The PDF beside it is where those two differ.
  const report = useCallback(async (reason: string) => setFailure(reason), []);
  const { busy, email, start } = useAdminExport({
    request,
    onReady: saveBundle,
    onUnavailable: report,
    onError: report,
  });

  return (
    <Button
      variant="outline"
      tone="neutral"
      size="sm"
      disabled={busy || !email}
      title={
        failure ??
        (email
          ? "Download the full agent-debug bundle (brief + trace) as JSON"
          : "Loading your employee identity…")
      }
      onClick={() => {
        setFailure(null);
        void start();
      }}
    >
      {busy ? "Exporting…" : "Export debug"}
    </Button>
  );
}

/** What this project was on: its A/B exposures (by its owner), the prompt
 *  versions its renders picked, and how many injection decisions it carries.
 *  Reads the experimentation lookup, which is a `system_admin` route, so it
 *  renders nothing for a viewer without that area rather than a 403 on every
 *  project page. Each name links to the experiment's own page. */
function ExperimentsStrip({ projectId }: { projectId: string }) {
  const holdsSystemAdmin = useHoldsAnyArea(["system_admin"]);
  const lookup = useQuery({
    ...projectExperimentsQuery(projectId),
    enabled: holdsSystemAdmin && projectId.length > 0,
  });
  // The PROJECT-unit half. `exposures` above answers the same question for the
  // USER unit and therefore only for the two product cohorts; every prompt
  // slot, injection and logic arm buckets on the project and lands here.
  const armRows = useQuery({
    ...projectArmsQuery(projectId),
    enabled: holdsSystemAdmin && projectId.length > 0,
  });
  if (!holdsSystemAdmin || lookup.isError) return null;
  const data = lookup.data;
  const exposures = data?.exposures ?? [];
  const approaches = Object.entries(data?.approaches ?? {});
  const decisions = data?.decisions.length ?? 0;
  const arms = armRows.data?.arms ?? [];
  return (
    <Card padding="md" className="ex-experiments">
      <h2 className="ad-card__title">Experiments</h2>
      {lookup.isPending ? <p className="ad-card__body">Reading exposures…</p> : null}
      {data &&
      arms.length === 0 &&
      exposures.length === 0 &&
      approaches.length === 0 &&
      decisions === 0 ? (
        <p className="ad-card__body">Not in any experiment.</p>
      ) : null}
      {arms.length > 0 ? (
        <ul className="ex-experiments__list">
          {arms.map((row) => (
            <li key={`arm:${row.experiment}`}>
              <Link className="ad-link" to="/experiments/$key" params={{ key: row.experiment }}>
                <code>{row.experiment}</code>
              </Link>{" "}
              in arm <strong>{row.arm}</strong>
              <span className="ex-experiments__why">{whyThisArm(row)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {exposures.length > 0 || approaches.length > 0 || decisions > 0 ? (
        <ul className="ex-experiments__list">
          {exposures.map((row, i) => {
            const key = typeof row.experiment === "string" ? row.experiment : "";
            return (
              <li key={`exposure:${i}`}>
                {key ? (
                  <Link className="ad-link" to="/experiments/$key" params={{ key }}>
                    <code>{key}</code>
                  </Link>
                ) : (
                  <code>-</code>
                )}{" "}
                assigned <strong>{String(row.assignedArm ?? "-")}</strong>, served{" "}
                <strong>{String(row.servedArm ?? "-")}</strong>
              </li>
            );
          })}
          {approaches.map(([slot, approach]) => (
            <li key={`approach:${slot}`}>
              <Link className="ad-link" to="/experiments/$key" params={{ key: slot }}>
                <code>{slot}</code>
              </Link>{" "}
              version <strong>{approach}</strong>
            </li>
          ))}
          {decisions > 0 ? (
            <li>
              {decisions} injection {decisions === 1 ? "decision" : "decisions"} recorded; the full
              list is on the{" "}
              <Link className="ad-link" to="/experiments">
                experiments register
              </Link>{" "}
              lookup.
            </li>
          ) : null}
        </ul>
      ) : null}
    </Card>
  );
}

/** "Why this arm": the bucket the project landed in, when, and what it WAS at
 *  that moment.
 *
 *  The attributes are the ones recorded at FIRST exposure, not the ones the
 *  project has now: a later segmentation must use the value at exposure, and
 *  there is no way to recover it afterwards. A value the platform does not hold
 *  is simply absent, which is why this sentence is assembled from what is there
 *  rather than printed from a fixed template with blanks in it.
 *
 *  The exact bucket NUMBER and the dial position at the time are not here yet:
 *  they are recorded on `injection_decisions` and are not on that table's
 *  per-project read. Until they are, the honest line is when and under what,
 *  rather than a fabricated "bucket 42".
 */
function whyThisArm(row: AdminProjectArm): string {
  const attrs = row.firstExposureAttrs ?? {};
  const said: string[] = [];
  for (const name of ["device", "entry_point", "country", "model_tier", "lane", "build_sha"]) {
    const value = (attrs as Record<string, unknown>)[name];
    if (typeof value === "string" && value.length > 0) said.push(`${name} ${value}`);
  }
  const when = new Date(row.firstSeenAt).toLocaleString();
  const context = said.length > 0 ? `, on ${said.join(", ")}` : "";
  return `Bucketed here on ${when}${context}. Last seen in this arm ${new Date(row.lastSeenAt).toLocaleString()}.`;
}

function ProjectDetailView(props: DetailViewProps) {
  const { projectId, projectName, owner, agentPaused, humanTakeover, openFlags, stage, tabs } =
    props;
  // Restored on a back hop too: a Sellers tab that opens a talent page has to be
  // the tab the operator lands back on, or the control sent them somewhere they
  // then have to navigate to a second time.
  const [tab, setTab] = useRestoredState<Tab>("explorer-project/tab", "Conversation");
  // A mode change (or a stale bookmark) must never leave a tab selected that
  // this viewer has no data for.
  const active = tabs.includes(tab) ? tab : tabs[0];

  return (
    <div className="ad-page">
      <header className="ex-detail__head">
        <BackLink to="/explorer" label="Back to Explorer" />
        <div className="ex-detail__title-row">
          <div>
            <div className="ex-detail__title">
              <h1 className="ad-page__title">{projectName}</h1>
              {/* Where this project actually got to. Beside the name because it
                  is the first thing an operator wants to know on landing here
                  from a Slack link or the Alerts worklist. Support mode's
                  payload has no stage, so it renders no pill rather than a
                  confident "New". */}
              {stage ? <ProjectStageBadge stage={stage} size="md" /> : null}
              {/* How the client wants to WORK on it, beside how far it got. */}
              <WorkShapeTag shape={props.workShape} />
              {/* Which model lane serves it (and the country that decided that). */}
              <ModelTierTag tier={props.modelTier} country={props.originCountry} />
              {/* The client walked out to search Fiverr by themselves at least
                  once. Next to the stage because it reads as part of the same
                  story: how far the project got, and whether the client stayed
                  in the assistant lane while it did. */}
              {props.selfSearchClicked ? (
                <Badge
                  tone="warning"
                  size="sm"
                  title="The client clicked a link to search Fiverr by themselves at least once."
                >
                  Searched Fiverr alone
                </Badge>
              ) : null}
            </div>
            <p className="ad-page__sub">
              {owner.id ? (
                <Link to="/explorer/users/$id" params={{ id: owner.id }} className="ex-owner-link">
                  {owner.label}
                </Link>
              ) : (
                "Unknown owner"
              )}
              {/* When the label is the client's NAME, keep the email beside it:
                  the name says who they are, the email is how a rep finds or
                  contacts the account. */}
              {owner.email && owner.email !== owner.label ? (
                <span className="ad-cell-sub ex-owner-email">{owner.email}</span>
              ) : null}
            </p>
          </div>
          {/* A viewer who may write gets the controls, which carry the two state
              badges themselves. Support mode gets the badges alone. */}
          {props.onChanged ? (
            <div className="ex-ops">
              <SellerIntentTag sellerIntent={props.sellerIntent} />
              {openFlags.length > 0 ? (
                <Badge tone="danger" size="sm">
                  {openFlags.length} open {openFlags.length === 1 ? "flag" : "flags"}
                </Badge>
              ) : null}
              <BriefDocumentButton projectId={projectId} projectName={projectName} />
              <ExportProjectButton projectId={projectId} projectName={projectName} />
              <OperatorControls
                projectId={projectId}
                agentPaused={agentPaused}
                humanTakeover={humanTakeover}
                onChanged={props.onChanged}
              />
              {/* Last, and the loudest thing in the corner: it is the only
                  control here that starts something irreversible for the client
                  (Mira messaging real talent), so it anchors the stack rather
                  than sitting in a row of table-sized buttons. Hangs off
                  `onChanged` like every other write, so support and viewer modes
                  never see it. */}
              <ApproveBriefButton
                projectId={projectId}
                checklist={props.briefChecklist}
                onChanged={props.onChanged}
              />
            </div>
          ) : (
            <div className="ex-badges">
              {props.readOnlyLabel ? (
                <Badge tone="neutral" size="sm" title="Your access to this console is read only.">
                  {props.readOnlyLabel}
                </Badge>
              ) : null}
              <SellerIntentTag sellerIntent={props.sellerIntent} />
              {/* Both downloads are READS, so they sit on both sides of the
                  read-only split rather than hanging off `onChanged` — a support
                  rep and a viewer need the client's brief as much as an operator
                  does, and the routes are plain employee scope. */}
              <BriefDocumentButton projectId={projectId} projectName={projectName} />
              <ExportProjectButton projectId={projectId} projectName={projectName} />
              <Badge tone={agentPaused ? "warning" : "success"} size="sm">
                {agentPaused ? "Agent paused" : "Agent active"}
              </Badge>
              {humanTakeover ? (
                <Badge tone="warning" size="sm">
                  Human takeover
                </Badge>
              ) : null}
              {openFlags.length > 0 ? (
                <Badge tone="danger" size="sm">
                  {openFlags.length} open {openFlags.length === 1 ? "flag" : "flags"}
                </Badge>
              ) : null}
            </div>
          )}
        </div>
      </header>

      {openFlags.length > 0 ? (
        <Card padding="md" className="ex-flags">
          <h2 className="ad-card__title">Open flags</h2>
          <ul>
            {openFlags.map((flag) => (
              <li key={flag.id}>
                <strong>{flag.reason.replaceAll("_", " ")}</strong>
                {flag.detail ? <span> {flag.detail}</span> : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <ExperimentsStrip projectId={projectId} />

      <div className="ex-tabs" role="tablist" aria-label="Project detail sections">
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={active === name}
            className={`ex-tab${active === name ? " ex-tab--active" : ""}`}
            onClick={() => setTab(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="ex-tab-panel" role="tabpanel">
        <TabPanel tab={active} props={props} />
      </div>
    </div>
  );
}

function FullProjectView({ projectId, acting }: { projectId: string; acting: boolean }) {
  const detail = useQuery(explorerProjectQuery(projectId));
  const queryClient = useQueryClient();

  // STABLE identity: the conversation panel schedules this from an effect keyed on
  // the callback, and it invalidates the very query whose new data re-renders the
  // panel — a fresh closure per render turns one live event into an endless loop.
  // Also refreshes the Big clients list, whose Stopped column this can change.
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: adminKeys.explorerProject(projectId) });
    void queryClient.invalidateQueries({ queryKey: adminKeys.bigClients });
  }, [queryClient, projectId]);

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading project…" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary area="explorer" error={detail.error} onRetry={() => void detail.refetch()} />
      </div>
    );
  }

  const data = detail.data;
  return (
    <ProjectDetailView
      projectId={projectId}
      // Viewer mode reads this whole record and may change none of it, so both
      // write signals go null: no live-spectate channel (that panel IS the
      // composer) and no re-read to schedule. Same shape support mode uses.
      channel={acting ? data.channel : null}
      onChanged={acting ? refresh : null}
      readOnlyLabel={acting ? null : "View only"}
      projectName={text(data.workspace["project"] as Record<string, unknown>, "name") ?? "Project"}
      // `clientName` is the account's display name, else the name the client
      // gave Mira; older backends without it fall back exactly as before.
      owner={{
        id: data.owner.id,
        label: data.owner.clientName || data.owner.displayName || data.owner.email || "Owner",
        email: data.owner.email,
      }}
      agentPaused={data.agentPaused}
      humanTakeover={data.humanTakeover}
      stage={data.stage}
      // The latched engagement shape, off the workspace payload (the wire model
      // is WorkspaceState; both spellings tolerated like every other read off
      // this loosely-typed bag).
      workShape={text(data.workspace, "workShape", "work_shape")}
      modelTier={data.modelTier ?? null}
      originCountry={data.originCountry ?? null}
      selfSearchClicked={data.selfSearchClicked ?? false}
      openFlags={data.openFlags.map((f) => ({ id: f.id, reason: f.reason, detail: f.detail }))}
      sellerIntent={data.sellerIntent ?? false}
      messages={data.fullMessages}
      briefChecklist={data.briefChecklist}
      briefSections={asArray(data.workspace, "sections").map((s, i) => ({
        id: text(s, "id") ?? String(i),
        title: text(s, "title") ?? "Untitled section",
        content: text(s, "content") ?? "",
      }))}
      files={asArray(data.workspace, "files").map((f, i) => ({
        id: text(f, "id") ?? String(i),
        name: text(f, "name") ?? "Unnamed",
        size: text(f, "size") ?? "-",
        uploadedAt: text(f, "uploadedAt", "uploaded_at"),
        // Absent on a legacy row that predates the scan column: treat it as clean
        // rather than hiding the download, since the route re-checks anyway.
        scanStatus: text(f, "scanStatus", "scan_status") ?? "clean",
      }))}
      downloadFile={(fileId) => fetchExplorerProjectFile(projectId, fileId)}
      tabs={TABS}
      full={data}
      canTranslate
    />
  );
}

function SupportProjectView({ projectId }: { projectId: string }) {
  const detail = useQuery(conversationQuery(projectId));

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading project…" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="customer_support"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const cs = detail.data;
  return (
    <ProjectDetailView
      projectId={projectId}
      // Read-only by backend policy (see the module header), so no channel to
      // spectate and no refetch hook: null is what makes the view read-only.
      channel={null}
      onChanged={null}
      readOnlyLabel={null}
      projectName={cs.project.name}
      owner={{
        id: cs.owner.id,
        label: cs.owner.clientName || cs.owner.displayName || cs.owner.email || "Customer",
        email: cs.owner.email ?? null,
      }}
      agentPaused={cs.agentPaused}
      humanTakeover={cs.humanTakeover}
      // The CS payload is an explicit allowlist (admin_support's
      // CONVERSATION_PAYLOAD_KEYS) and carries none of these. Absent, not
      // hidden.
      stage={null}
      workShape={null}
      modelTier={null}
      originCountry={null}
      selfSearchClicked={false}
      briefChecklist={null}
      openFlags={cs.openFlags.map((f) => ({
        id: f.id,
        reason: f.reason,
        detail: f.detail ?? null,
      }))}
      sellerIntent={cs.sellerIntent ?? false}
      messages={cs.messages}
      briefSections={cs.brief.map((s) => ({
        id: s.id,
        title: s.title,
        content: s.content ?? "",
      }))}
      files={cs.files.map((f) => ({
        id: f.id,
        name: f.name,
        size: f.size,
        uploadedAt: f.uploadedAt,
        scanStatus: f.scanStatus,
      }))}
      downloadFile={(fileId) => fetchSupportProjectFile(projectId, fileId)}
      tabs={SUPPORT_TABS}
      full={null}
      canTranslate={false}
    />
  );
}

export function ExplorerProjectPage({ projectId }: { projectId: string }) {
  const mode = useExplorerMode();
  return mode === "support" ? (
    <SupportProjectView projectId={projectId} />
  ) : (
    <FullProjectView projectId={projectId} acting={canAct(mode)} />
  );
}
